/** Ctrl/⌘K search (spec §10.3): screens the user may open, plus record search from /api/v1/search/. */
import { useQuery } from '@tanstack/react-query'
import { Command } from 'cmdk'
import { Search } from 'lucide-react'
import { type CSSProperties, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { api, unwrap } from '@/lib/api/client'
import { useMe } from '@/lib/auth'
import { t } from '@/lib/text'
import { visibleRoutes } from './routes'

const MIN_CHARS = 2

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms)
    return () => window.clearTimeout(id)
  }, [value, ms])
  return debounced
}

/** The part of the screen the on-screen keyboard leaves free, kept current while it opens and closes. */
function useVisibleArea(active: boolean): { height: number; top: number } | null {
  const [area, setArea] = useState<{ height: number; top: number } | null>(null)
  useEffect(() => {
    const viewport = window.visualViewport
    if (!active || !viewport) return
    const update = () => setArea({ height: Math.round(viewport.height), top: Math.round(viewport.offsetTop) })
    update()
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
    }
  }, [active])
  return area
}

const itemClasses =
  'flex cursor-default select-none items-center gap-2 rounded-md px-3 py-2 text-sm data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground max-md:min-h-11'

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const me = useMe()
  const navigate = useNavigate()
  const area = useVisibleArea(open)
  const [query, setQuery] = useState('')
  const q = useDebounced(query.trim(), 200)
  const { data } = useQuery({
    queryKey: ['search', q],
    queryFn: async () => unwrap(await api.GET('/api/v1/search/', { params: { query: { q } } })),
    enabled: open && q.length >= MIN_CHARS,
    staleTime: 10_000,
  })

  function go(path: string) {
    onOpenChange(false)
    setQuery('')
    navigate(path)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* On a phone the panel sits at the top edge and fits above the keyboard; from md up it floats near the top. */}
      <DialogContent
        closeLabel={t('core.shell.close')}
        className="top-[var(--search-top,0px)] max-h-[var(--search-height,100dvh)] w-full translate-y-0 gap-0 overflow-hidden rounded-none border-x-0 border-t-0 p-0 md:top-[15%] md:max-h-[calc(100dvh-2rem)] md:w-[calc(100%-2rem)] md:max-w-xl md:rounded-lg md:border"
        style={area ? ({ '--search-height': `${area.height}px`, '--search-top': `${area.top}px` } as CSSProperties) : undefined}
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">{t('core.palette.title')}</DialogTitle>
        <Command shouldFilter={false} label={t('core.palette.title')}>
          <div className="flex items-center gap-2 border-b px-3">
            <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder={t('core.palette.placeholder')}
              className="h-12 w-full bg-transparent text-base outline-none placeholder:text-muted-foreground md:text-sm"
            />
          </div>
          <Command.List className="max-h-[calc(var(--search-height,100dvh)-3rem)] overflow-y-auto overscroll-contain p-2 md:max-h-[60dvh]">
            <Command.Group heading={t('core.palette.screens')} className="text-xs text-muted-foreground [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
              {visibleRoutes(me.permissions)
                .filter((route) => !q || t(route.nav!.labelKey).toLowerCase().includes(q.toLowerCase()))
                .map((route) => {
                  const Icon = route.nav!.icon
                  return (
                    <Command.Item key={route.path} value={`screen:${route.path}`} onSelect={() => go(route.path)} className={itemClasses}>
                      <Icon className="size-4" aria-hidden />
                      {t(route.nav!.labelKey)}
                    </Command.Item>
                  )
                })}
            </Command.Group>
            {data?.groups.map((group) => (
              <Command.Group key={group.key} heading={group.label} className="text-xs text-muted-foreground [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
                {group.items.map((record) => (
                  <Command.Item key={`${group.key}:${record.id}`} value={`${group.key}:${record.id}`} onSelect={() => go(record.url)} className={itemClasses}>
                    <span className="min-w-0 flex-1 truncate text-foreground">{record.title}</span>
                    <span className="truncate text-muted-foreground">{record.subtitle}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
            {q.length > 0 && q.length < MIN_CHARS && <p className="px-3 py-2 text-xs text-muted-foreground">{t('core.palette.min_chars')}</p>}
            <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">{t('core.palette.empty')}</Command.Empty>
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
